import ButtonBox from "component/ButtonBox";
import InputBox from "component/InputBox";
import InputError from "component/InputError";
import Text from "component/Text";
import UserListViewController from "controller/teams/UserListViewController";
import { motion } from "framer-motion";
import { useAppSelector } from "hook/redux";
import React, { useCallback, useReducer, useRef } from "react";
import { useNavigate } from "react-router-dom";
import SignUpReducer, { INITIAL_SIGNUP_STATE } from "reducer/auth/SignUpReducer";
import { linkPageLogin } from "routes";
import SimpleReactValidator from "simple-react-validator";
import { ViewRenderStatus } from "util/CommonConstant";
import {
  getStandardValidator,
  messageReplace,
  parseStandardAPIErrorMessage,
} from "util/CommonUtil";

// Removed the empty interface declaration

const SignUpForm = () => {
  const isAuth0Enabled = useAppSelector((state) => state.configCat.enableAuth0);

  const navigate = useNavigate(); // Initialize navigation hook
  const [signUpState, dispatch] = useReducer(SignUpReducer, {
    ...INITIAL_SIGNUP_STATE,
  });
  // Create an instance of LoginController to manage form actions
  const signUpController = new UserListViewController(signUpState, dispatch);

  const simpleValidator = useRef(
    new SimpleReactValidator({
      validators: { ...getStandardValidator() },
      ...messageReplace(),
      autoForceUpdate: {
        forceUpdate: () => signUpController.forceUpdate(!signUpState.forceUpdate),
      },
    })
  );

  /* 
      -----------------------------------------------------------------------------------------------------------------------
      Validators:
      -----------------------------------------------------------------------------------------------------------------------
    */

  const validate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    simpleValidator?.current?.showMessageFor(e?.target?.name);
  }, []);

  // Function to validate the form
  const validateForm = useCallback(() => {
    let isValid = false; // Initialize isValid flag
    isValid = simpleValidator?.current?.allValid(); // Check if all fields are valid
    if (isValid) {
      // If form is valid
      return true; // Return true
    } else {
      // If form is not valid
      signUpController.forceUpdate(!signUpState.forceUpdate); // Force update to show errors
      simpleValidator?.current?.showMessages(); // Show error messages
      signUpController.forceUpdate(!signUpState.forceUpdate); // Force update again to ensure errors are displayed
      return false; // Return false
    }
  }, []);

  // Event handlers for form inputs
  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      signUpController.handleInputChange(event);
    },
    [signUpController]
  );

  // Handle form submission
  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event?.preventDefault(); // Prevent default form submission behavior

      if (!validateForm()) {
        return Promise.reject(new Error("Form validation failed")); // Reject promise if form validation fails
      }

      try {
        const signUpResponse = await signUpController.signUp(isAuth0Enabled);
        // Perform login action
        if (signUpResponse.response?.error_message === ViewRenderStatus.SUCCESS) {
          navigate(linkPageLogin); // Redirect user on successful signUp
        } else {
          signUpController.setErrorMessage(signUpResponse.response?.error_message ?? ""); // Set error message if login response status is not 200
        }
      } catch (error: any) {
        const errorMessage =
          parseStandardAPIErrorMessage(error) ?? error?.message ?? "An error occurred"; // Parse and set error message
        signUpController.setErrorMessage(errorMessage); // Set error message in case of an error
        return Promise.reject(new Error("Error")); // Reject promise with an error message
      }
    },
    [isAuth0Enabled, navigate, signUpController, validateForm]
  ); // Dependencies for the handleSubmit function

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      exit={{ opacity: 0, y: 20 }}
      className="w-full rounded-xl bg-light-grey-50 px-10 py-12 shadow-light-black-transparent-25 backdrop-blur-xs  dark:bg-primary-login-card dark:shadow-dark-black-transparent-25 sm:w-3/5 lg:w-129 lg:px-16"
    >
      <div className="mb-9 text-center text-heading-2 font-light">
        <Text label="label.text.signUp.heading" />
      </div>

      <div className="mb-3 flex flex-col gap-y-3">
        <InputBox
          label="label.text.signUp.firstName"
          placeholder="Enter First Name"
          name="firstName"
          onChange={handleInputChange}
          value={signUpState.data.firstName}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "firstName",
            signUpState.data.firstName,
            "mandatory"
          )}
        />

        <InputBox
          label="label.text.signUp.lastName"
          placeholder="Enter Last Name"
          name="lastName"
          onChange={handleInputChange}
          value={signUpState.data.lastName}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "lastName",
            signUpState.data.lastName,
            "mandatory"
          )}
        />

        <InputBox
          label="label.text.signUp.email"
          placeholder="Enter email"
          name="email"
          onChange={handleInputChange}
          value={signUpState.data.email}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "email",
            signUpState.data.email,
            "mandatory|email"
          )}
        />

        <InputBox
          label="label.text.signUp.newPassword"
          placeholder="Set a strong password"
          name="password"
          type="password"
          onChange={handleInputChange}
          value={signUpState.data.password}
          onBlur={validate}
          validation={simpleValidator?.current?.message(
            "password",
            signUpState.data.password,
            "mandatory"
          )}
        />
      </div>

      <ButtonBox
        label="label.button.signUp"
        color="gradient-blue"
        onClickWithLoader={handleSubmit}
        // enableMaskWhileLoading
      />
      <InputError
        validation={signUpState.errorMessage}
        errorClassNames="mt-1 flex justify-center"
      />
    </motion.div>
  );
};

export default SignUpForm;
